/**
 * Cookie Management Utility
 * Handles secure cookie operations for session tokens
 * Best practices:
 * - HttpOnly not available on client-side, so using secure flag
 * - SameSite protection against CSRF
 * - 1-day expiration for security
 * - Path scoping to prevent leakage
 */

interface CookieOptions {
  maxAge?: number; // seconds
  path?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

const DEFAULT_MAX_AGE = 86400; // 24 hours in seconds
const DEFAULT_PATH = '/';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

/**
 * Set a cookie with secure defaults
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {}
): void {
  const {
    maxAge = DEFAULT_MAX_AGE,
    path = DEFAULT_PATH,
    secure = IS_PRODUCTION,
    sameSite = 'Strict',
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (maxAge) {
    cookieString += `; Max-Age=${maxAge}`;
  }

  if (path) {
    cookieString += `; Path=${path}`;
  }

  if (secure) {
    cookieString += '; Secure';
  }

  if (sameSite) {
    cookieString += `; SameSite=${sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Get a cookie by name
 */
export function getCookie(name: string): string | null {
  const nameEQ = encodeURIComponent(name) + '=';
  const cookies = document.cookie.split(';');

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(nameEQ)) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
}

/**
 * Delete a cookie
 */
export function deleteCookie(name: string, path: string = DEFAULT_PATH): void {
  setCookie(name, '', {
    maxAge: 0,
    path,
  });
}

/**
 * Get all cookies as an object
 */
export function getAllCookies(): Record<string, string> {
  const cookies: Record<string, string> = {};
  const cookieArray = document.cookie.split(';');

  for (let cookie of cookieArray) {
    cookie = cookie.trim();
    const [name, value] = cookie.split('=');

    if (name && value) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  }

  return cookies;
}

/**
 * Clear all session cookies
 */
export function clearSessionCookies(): void {
  deleteCookie('idToken');
  deleteCookie('accessToken');
  deleteCookie('refreshToken');
}
